import { Controller } from "@hotwired/stimulus"
import { conditionKey, fromPicker, toQuery, toParams } from "../lib/filter_conditions"
import { csrfToken } from "../lib/csrf"

// The condition builder (channel processing spec 3.6 and 5.3): attribute, operator, value rows
// added as chips joined by "and". The server describes every stored condition (label, operator
// label, and, for a pre-filter, whether it is ignored and why); this controller only adds,
// removes, and serialises. Two homes:
//
// - The wizard's Pre-Filter step (mode "json"), with the index's chrome: the Saved searches
//   dropdown sets the reference (the draft reads the search's query live). Both pre-filter
//   fields are posted as hidden inputs: the search id for an unedited reference, otherwise the
//   two-part query JSON (the way workflow_definition is) and a blank id. Ignored conditions
//   render as amber strikethrough chips with the reason under the box. Editing a referenced
//   search's chips mirrors the index: the label shows the search as edited and Update "<name>"
//   (PATCH, every channel reading the search follows) or Save as new are offered; proceeding
//   with neither keeps the edited conditions as the channel's own.
// - The Reports index (mode "params"): one q[...] or results[...][...] input per condition inside
//   the search form (a form field that already owns a key, the free-text box, is set instead),
//   submitted on every change. The result attributes are fetched when the builder opens.
//
//   data-filter-conditions-attributes-value='[{"name":"title","label":"title","type":"text","part":"q","predicates":[["cont","contains"], ...]}, ...]'
//   data-filter-conditions-attributes-url-value="/reports/filter_attributes"   (index: the full list, fetched once)
//   data-filter-conditions-conditions-value='[{"part":"q","attribute":"title","label":"title","predicate":"cont","predicate_label":"contains","value":"sql","ignored":false,"reason":null}]'
//   data-filter-conditions-mode-value="json|params"
//   data-filter-conditions-field-name-value="channel_draft[prefilter_query]"
//   data-filter-conditions-search-field-name-value="channel_draft[prefilter_saved_search_id]"
//   data-filter-conditions-search-value='{"id":"…","name":"Critical SQL"}'   (the reference, or null)
//   data-filter-conditions-count-url-value="/channel_wizard/:id/prefilter_count"
//   data-filter-conditions-total-value="203"
//   data-filter-conditions-save-url-value="/saved_searches"
// The attributes a cloaked report keeps encrypted, mirroring Channel::Prefilter (SMA-121).
const ENCRYPTABLE_ATTRIBUTES = ["description", "title_or_description"]

export default class extends Controller {
  static targets = ["tokens", "attribute", "operator", "value", "valueSelect", "picker", "fields", "count", "notes", "builder", "badge",
                    "searchLabel", "menu", "menuList", "searchOption", "updateSearch", "saveAs", "savePanel", "saveName", "saveError"]
  static values = {
    attributes: Array, attributesUrl: String, conditions: Array, mode: { type: String, default: "json" },
    fieldName: String, searchFieldName: String, search: Object, countUrl: String, total: Number, saveUrl: String
  }

  connect() {
    this.conditions = this.conditionsValue
    this.attributes = this.attributesValue
    // The referenced search, { id, name } or null; `edited` once its chips differ from it.
    this.search = this.searchValue?.id ? this.searchValue : null
    this.edited = false
    // The picker waits for the fetched attribute list (the index) before it can be used.
    if (this.hasPickerTarget) this.pickerTarget.disabled = Boolean(this.attributesUrlValue)
    this.attributeChanged()
    this.render()
    this.connected = true
    this.outsideClick = (event) => { if (this.hasMenuTarget && !this.element.contains(event.target)) this.menuTarget.classList.add("hidden") }
    document.addEventListener("click", this.outsideClick)
  }

  disconnect() {
    clearTimeout(this.timer)
    document.removeEventListener("click", this.outsideClick)
  }

  attributeChanged() {
    const attribute = this.currentAttribute()
    if (!attribute) return
    this.operatorTarget.replaceChildren(...attribute.predicates.map(([predicate, label]) => this.option(predicate, label)))
    this.valueTarget.type = attribute.type === "datetime" ? "date" : "text"
    // A choice list (briefs, enum values) is a select; a list predicate makes it a multi-select.
    const choice = Boolean(attribute.options)
    this.valueTarget.classList.toggle("hidden", choice)
    this.valueSelectTarget.classList.toggle("hidden", !choice)
    if (choice) this.valueSelectTarget.replaceChildren(...attribute.options.map(([value, label]) => this.option(value, label)))
  }

  // A list predicate (marked by the server on the predicate) takes several values; they are
  // chosen one at a time from the dropdown and accumulate into one chip.
  listPredicate(attribute, predicate) {
    return Boolean(attribute?.predicates.find(([p]) => p === predicate)?.[2])
  }

  add(event) {
    event.preventDefault()
    const attribute = this.currentAttribute()
    const predicate = this.operatorTarget.value
    const value = attribute && this.currentValue(attribute, predicate)
    if (!attribute || !predicate || !value?.length) return
    let condition = fromPicker(attribute, predicate, value)
    const existing = this.conditions.find((c) => conditionKey(c) === conditionKey(condition))
    if (existing && Array.isArray(existing.value)) {
      condition = fromPicker(attribute, predicate, Array.from(new Set([...existing.value, ...value])))
    }
    this.replace(this.conditions.filter((c) => conditionKey(c) !== conditionKey(condition)).concat(condition))
    this.valueTarget.value = ""
  }

  // The chosen value: the text box, or the select's choices (a list for a list predicate).
  currentValue(attribute, predicate) {
    if (!attribute.options) return this.valueTarget.value.trim()
    const chosen = Array.from(this.valueSelectTarget.selectedOptions).map((o) => o.value)
    return this.listPredicate(attribute, predicate) ? chosen : chosen[0]
  }

  remove(event) {
    event.preventDefault()
    const key = event.currentTarget.dataset.key
    this.replace(this.conditions.filter((c) => conditionKey(c) !== key))
  }

  // Enter in the value box adds the condition instead of submitting the form.
  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.add(event)
    }
  }

  // An edit by the person. In json mode an edit to a referenced search's chips makes the
  // conditions the channel's own until the search is updated or saved as new; in params mode
  // the page reloads with the new query.
  replace(conditions) {
    this.conditions = conditions
    if (this.search) this.edited = true
    if (this.modeValue === "params") {
      this.renderFields()
      this.fieldsTarget.closest("form")?.requestSubmit()
      return
    }
    this.render()
  }

  // The index's Conditions button shows or hides the builder, fetching the result attributes once.
  async toggleBuilder(event) {
    event.preventDefault()
    this.builderTarget.classList.toggle("hidden")
    if (this.builderTarget.classList.contains("hidden")) return
    await this.loadAttributes()
    this.valueTarget.focus()
  }

  async loadAttributes() {
    if (!this.attributesUrlValue || this.attributesLoaded) return
    const response = await fetch(this.attributesUrlValue, { headers: { Accept: "application/json" } })
    if (!response.ok) return
    this.attributes = await response.json()
    this.attributesLoaded = true
    const present = new Set(Array.from(this.attributeTarget.options).map((o) => o.value))
    this.attributes.filter((a) => !present.has(a.name)).forEach((a) => this.attributeTarget.appendChild(this.option(a.name, a.label)))
    // The fetched entries carry the choice lists; refresh the controls for the current attribute.
    this.attributeChanged()
    this.pickerTarget.disabled = false
  }

  // ---- saved searches (the wizard's dropdown) ----

  toggleMenu(event) {
    event.preventDefault()
    this.menuTarget.classList.toggle("hidden")
  }

  // A search picked from the dropdown: its conditions are the chips. Custom (the first entry,
  // no id) picked: the chips stay and are the channel's own.
  pickSearch(event) {
    const option = event.currentTarget
    if (option.dataset.searchId) {
      this.search = { id: option.dataset.searchId, name: option.dataset.searchName }
      this.conditions = option.dataset.conditions ? JSON.parse(option.dataset.conditions) : []
    } else {
      this.search = null
    }
    this.edited = false
    this.menuTarget.classList.add("hidden")
    this.render()
  }

  // Update "<name>": the edited chips replace the search's query; the draft references it again.
  async updateSearch(event) {
    event.preventDefault()
    if (!(this.search && this.edited)) return
    const body = await this.postSearch(`${this.saveUrlValue}/${this.search.id}`, "PATCH", { query: toQuery(this.conditions) }, "Could not update the search.")
    if (body) this.adoptSearch(body)
  }

  // The dropdown gains or refreshes `search` (a picker entry: id, name, conditions), the draft
  // references it, and the chips are its.
  adoptSearch(search) {
    let option = this.searchOptionTargets.find((o) => o.dataset.searchId === search.id)
    if (!option) {
      const item = document.createElement("li")
      option = this.searchOptionTargets[0].cloneNode(true)
      option.dataset.searchId = search.id
      option.dataset.searchName = search.name
      option.textContent = search.name
      item.appendChild(option)
      this.menuListTarget.appendChild(item)
    }
    option.dataset.conditions = JSON.stringify(search.conditions)
    this.search = { id: search.id, name: search.name }
    this.edited = false
    this.conditions = search.conditions
    this.render()
  }

  // A saved search write; the parsed body, or null after showing the errors in the save panel.
  async postSearch(url, method, payload, fallback) {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ saved_search: payload })
    })
    const body = await response.json().catch(() => ({}))
    if (response.ok) return body
    this.saveErrorTarget.textContent = (body.errors || [fallback]).join(" ")
    this.savePanelTarget.classList.remove("hidden")
    return null
  }

  openSave(event) {
    event.preventDefault()
    this.savePanelTarget.classList.remove("hidden")
    this.saveErrorTarget.textContent = ""
    this.saveNameTarget.focus()
  }

  // Enter in the name box saves; Escape closes the panel.
  saveKeydown(event) {
    if (event.key === "Enter") { event.preventDefault(); this.confirmSave(event) }
    if (event.key === "Escape") { event.preventDefault(); this.cancelSave(event) }
  }

  cancelSave(event) {
    event.preventDefault()
    this.savePanelTarget.classList.add("hidden")
  }

  async confirmSave(event) {
    event.preventDefault()
    const name = this.saveNameTarget.value.trim()
    if (!name) { this.saveErrorTarget.textContent = "A name is required."; return }
    const body = await this.postSearch(this.saveUrlValue, "POST", { name, query: toQuery(this.conditions) }, "Could not save the search.")
    if (!body) return
    this.saveNameTarget.value = ""
    this.savePanelTarget.classList.add("hidden")
    this.adoptSearch(body)
  }

  // ---- rendering ----

  render() {
    this.renderChips()
    this.renderNotes()
    this.renderFields()
    this.renderSaveAs()
    this.renderBadge()
    this.scheduleCount()
  }

  renderChips() {
    const tokens = this.tokensTarget
    tokens.replaceChildren()
    if (!this.conditions.length) {
      tokens.appendChild(this.span("text-xs text-gray-400 px-1", "No conditions, every report matches"))
    }
    this.conditions.forEach((condition, index) => {
      if (index > 0) tokens.appendChild(this.span("text-xs text-gray-400", "and"))
      const chip = document.createElement("span")
      chip.dataset.attribute = condition.attribute
      chip.dataset.predicate = condition.predicate
      chip.dataset.part = condition.part
      chip.className = condition.ignored
        ? "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border bg-amber-50 border-amber-200 text-amber-800 line-through decoration-amber-400 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300"
        : "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-600 text-gray-900 dark:text-gray-100"
      chip.append(
        this.span("font-mono", condition.label),
        this.span(condition.ignored ? "text-amber-500" : "text-gray-400", condition.predicate_label),
        this.span("font-mono", condition.value_label)
      )
      const remove = document.createElement("button")
      remove.type = "button"
      remove.dataset.key = conditionKey(condition)
      remove.dataset.action = "filter-conditions#remove"
      remove.className = "ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      remove.innerHTML = '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg><span class="sr-only">Remove</span>'
      chip.appendChild(remove)
      tokens.appendChild(chip)
    })
  }

  renderNotes() {
    if (!this.hasNotesTarget) return
    const notes = this.conditions.filter((c) => c.reason).map((c) => c.reason)
    // A pre-filter reading a field the reporter encrypted takes no cloaked report at all: the
    // condition can never match one, so the channel skips them (SMA-121).
    if (this.modeValue === "json" && this.conditions.some((c) => ENCRYPTABLE_ATTRIBUTES.includes(c.attribute))) {
      notes.push("This channel will not receive cloaked reports.")
    }
    this.notesTarget.replaceChildren(...notes.map((text) => {
      const li = document.createElement("li")
      li.textContent = text
      return li
    }))
  }

  renderFields() {
    if (this.modeValue === "params") {
      const form = this.fieldsTarget.closest("form")
      const inputs = []
      for (const [name, value] of toParams(this.conditions)) {
        // A field the form already has for this key (the free-text box) is set, not duplicated.
        const owned = form?.elements.namedItem(name)
        if (owned && !this.fieldsTarget.contains(owned)) { owned.value = value; continue }
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = value
        inputs.push(input)
      }
      this.fieldsTarget.replaceChildren(...inputs)
      return
    }
    // Both pre-filter fields: the search id for an unedited reference, else the query.
    const reference = this.search && !this.edited
    const fields = [[this.fieldNameValue, reference ? "" : JSON.stringify(toQuery(this.conditions))]]
    if (this.searchFieldNameValue) fields.push([this.searchFieldNameValue, reference ? this.search.id : ""])
    this.fieldsTarget.replaceChildren(...fields.map(([name, value]) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      return input
    }))
  }

  // The dropdown label and the buttons: the search's name, "(edited)" with Update "<name>" and
  // Save as new while its chips are edited, Save search for custom chips, nothing for a reference.
  renderSaveAs() {
    if (!this.hasSaveAsTarget) return
    const edited = Boolean(this.search && this.edited)
    const custom = !this.search || edited
    if (this.hasSearchLabelTarget) {
      const name = this.search ? this.search.name : this.searchOptionTargets[0].dataset.searchName
      this.searchLabelTarget.textContent = edited ? `${name} (edited)` : name
    }
    if (this.hasUpdateSearchTarget) {
      this.updateSearchTarget.textContent = edited ? `Update "${this.search.name}"` : ""
      this.updateSearchTarget.classList.toggle("hidden", !(edited && this.conditions.length))
    }
    this.saveAsTarget.textContent = edited ? "Save as new" : "Save search"
    this.saveAsTarget.classList.toggle("hidden", !(custom && this.conditions.length))
  }

  renderBadge() {
    if (!this.hasBadgeTarget) return
    this.badgeTarget.textContent = this.conditions.length
    this.badgeTarget.classList.toggle("hidden", !this.conditions.length)
  }

  // ---- the live count (the wizard) ----

  scheduleCount() {
    if (!this.hasCountTarget || !this.countUrlValue) return
    clearTimeout(this.timer)
    if (!this.conditions.length) {
      this.countTarget.textContent = ""
      return
    }
    // The step renders the count for its stored query; only a change needs a fetch.
    if (!this.connected) return
    this.timer = setTimeout(() => this.fetchCount(), 250)
  }

  async fetchCount() {
    const params = new URLSearchParams({ query: JSON.stringify(toQuery(this.conditions)) })
    try {
      const response = await fetch(`${this.countUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (!response.ok) return
      const { matched } = await response.json()
      this.countTarget.textContent = `${matched} of ${this.totalValue} reports match today.`
    } catch (_error) {
      this.countTarget.textContent = ""
    }
  }

  // ---- helpers ----

  currentAttribute() {
    return this.attributes.find((a) => a.name === this.attributeTarget.value)
  }

  option(value, label) {
    const option = document.createElement("option")
    option.value = value
    option.textContent = label
    return option
  }

  span(className, text) {
    const span = document.createElement("span")
    span.className = className
    span.textContent = text
    return span
  }

}
